import { env } from "@oefr/env/client"
import { apiPlugin, getStoryblokApi, storyblokInit } from "@storyblok/react"

type StoryblokInitOptions = Parameters<typeof storyblokInit>[0]
type StoryblokComponents = NonNullable<StoryblokInitOptions>["components"]

/**
 * Without `clear: "auto"`, the client pins the cache version (`cv`) captured at boot and a long-running server keeps serving pre-publish content.
 */
const API_OPTIONS = {
    cache: { type: "memory", clear: "auto" },
} as const satisfies NonNullable<StoryblokInitOptions>["apiOptions"]

let isInitialized = false

/**
 * Registers the API client, and the bloks when rendering Storyblok content, on the global singleton `@storyblok/react` reads from.
 */
export function initStoryblok(components?: StoryblokComponents) {
    storyblokInit({
        accessToken: env.NEXT_PUBLIC_STORYBLOK_ACCESS_TOKEN_FR,
        use: [apiPlugin],
        apiOptions: API_OPTIONS,
        components,
    })

    isInitialized = true
}

/**
 * Returns the Storyblok API client, initializing it on first use.
 *
 * Use this over the library's `getStoryblokApi`, which returns the raw singleton: it is `null` until `storyblokInit` has run in the current process, which never happens on entry points that don't load a Storyblok layout (API routes, `getServerSideProps`).
 *
 * Bloks are left out on purpose: this path only performs API calls, and the component map would drag the whole component tree into those bundles.
 */
export function getSbApi() {
    if (!isInitialized) {
        initStoryblok()
    }

    return getStoryblokApi()
}
